// Sites page screens. Pure: data in, HTML out.
// Words: SITES_PAGE_WORDS.md (approved 2026-10-01), DEMO_SET_WORDS.md (approved 2026-10-05).
// Mockup: MOCKUP_SITES_APPROVED.html. Wide screens use the account-page layout;
// phones get the same content stacked (styles.css).
// Every change goes through server code (functions/src/sites.ts) and logs who and when.

import { withAdminBack } from "./admin-way-back";
import {
  CONTACT_ROLES,
  DEMO_TEMPLATES,
  USED_FOR,
  groupAssets,
  needsPlantManager,
  siteContacts,
  sortSites,
  type AssetGroup,
  type AssetRow,
  type Contact,
  type ContactInput,
  type Site,
  type SiteDetailsInput,
} from "./sites";
import { esc, ident } from "./training-screens";
import { usDate } from "./training-view";
import type { SitesData } from "./firebase";

type DemoDoc = SitesData["demo"][string];

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

function activePill(isActive: boolean): string {
  return `<span class="pill ${isActive ? "green" : "grey"}">${isActive ? "ACTIVE" : "OFF"}</span>`;
}

function place(s: Site): string {
  return [s.city, s.state].filter(Boolean).join(", ");
}

// ---- 2. Site list ----
// Gone in Home rev 3 (2026-10-23): Home is the site list. The Demo Set row moved to Program Setup.

// ---- 3 and 8. One site ----

export function siteDetail(site: Site, data: SitesData, assets: AssetRow[] | null, flash = ""): string {
  const contacts = siteContacts(data.contacts, site.siteNumber);
  const n = data.assetCounts[site.siteNumber] ?? 0;
  const locs = assets ? ` · ${count(groupAssets(assets).length, "location")}` : "";
  const pm = needsPlantManager(site, data.contacts);
  const contactRows = contacts
    .map(
      (c) => `<a class="ct-row${c.isActive ? "" : " dim"}" href="#/sites/${esc(site.siteNumber)}/contacts/${esc(c.contactId)}">
        <span class="t"><b>${esc(c.name || c.email)}</b><small>${esc(c.role)}${c.email ? ` · ${esc(c.email)}` : ""}</small>
          <span class="tags">${USED_FOR.map((u) => `<span class="tg${c.usedFor?.[u.key] ? "" : " off"}">${esc(u.label)}</span>`).join("")}</span></span>
        ${activePill(c.isActive)}</a>`,
    )
    .join("");
  const empty = contacts.length
    ? pm
      ? '<div class="empty">No Plant Manager yet. Add one so escalations have somewhere to go.</div>'
      : ""
    : '<div class="empty">No contacts yet. Add the plant manager and anyone who should get alerts or approve permits.</div>';
  return `<a class="back" href="#/sites">Sites</a>
    ${ident(site.siteNumber, site.name, place(site))}
    ${flash}
    ${site.isActive ? "" : '<div class="note">Not active. Scans here show the guest page. Switches on at cutover.</div>'}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Details</b><small>Site number is locked. It starts every Asset ID here.</small></div>
        <a class="chg" href="#/sites/${esc(site.siteNumber)}/edit">Change</a></div>
      <div class="rows">
        ${row("Site No.", `<span class="mono">${esc(site.siteNumber)}</span>`, '<span class="lock">Locked</span>')}
        ${row("Name", esc(site.name))}
        ${row("City", esc(site.city) || '<span class="muted">Not set</span>')}
        ${row("State", esc(site.state) || '<span class="muted">Not set</span>')}
        ${row("Arc Flash Assessed", site.arcFlashAssessedOn ? usDate(site.arcFlashAssessedOn) : '<span class="muted">Not set</span>')}
        ${row(
          "Active",
          `${activePill(site.isActive)} <small class="muted">${site.isActive ? "Labels at this site open the full app." : "Off. Guest page only."}</small>`,
          `<button class="chg" type="button" data-act="site-active">${site.isActive ? "Turn Off" : "Turn On"}</button>`,
        )}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>Site Contacts</b><small>${count(contacts.filter((c) => c.isActive).length, "active contact")}</small></div>
        <a class="chg" href="#/sites/${esc(site.siteNumber)}/contacts/new">Add Contact</a></div>
      ${contactRows}${empty}
    </div>
    <a class="bigbtn" href="#/sites/${esc(site.siteNumber)}/assets"><b>Assets</b><small>${count(n, "asset")}${locs} ›</small></a>`;
}

// ---- 7. The Active switch ----

export function activeSheet(site: Site, busy: boolean, error = ""): string {
  const off = site.isActive;
  const body = off
    ? `Labels scanned at ${site.name} will open the guest page with a "site not active" notice. QEWs cannot start a JSP there until you turn it back on. Assets and records are kept.`
    : `Labels scanned at ${site.name} will open the full app. QEWs at this site can sign in and work from their labels.`;
  return sheet(
    `${off ? "Turn off" : "Turn on"} ${site.name}?`,
    `<p>${esc(body)}</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go${off ? " red" : ""}" type="button" data-act="site-active-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : off ? "Turn Off Site" : "Turn On Site"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">${off ? "Keep Active" : "Keep Off"}</button>`,
  );
}

// ---- Details edit ----

export function siteEditForm(site: Site, entry: SiteDetailsInput | null, today: string, busy: boolean, error = ""): string {
  const v = entry ?? { name: site.name, city: site.city, state: site.state, arcFlashAssessedOn: site.arcFlashAssessedOn ?? "" };
  return `<a class="back" href="#/sites/${esc(site.siteNumber)}">Cancel</a>
    ${ident(site.siteNumber, "Change Details", site.name)}
    <form class="box" id="site-form" data-site="${esc(site.siteNumber)}" novalidate>
      <div class="rows">
        ${row("Site No.", `<span class="mono">${esc(site.siteNumber)}</span>`, '<span class="lock">Locked</span>')}
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(v.name)}" maxlength="60" required></span><span class="e"></span></label>
        <label class="r"><span class="k">City</span><span class="v"><input class="fin wide" name="city" value="${esc(v.city)}" maxlength="60" required></span><span class="e"></span></label>
        <label class="r"><span class="k">State</span><span class="v"><input class="fin num" name="state" value="${esc(v.state)}" maxlength="2" autocapitalize="characters" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Arc Flash Assessed</span><span class="v"><input class="fin" type="date" name="arcFlashAssessedOn" value="${esc(v.arcFlashAssessedOn)}" max="${today}"><small class="fhint">Leave blank if no assessment is on record.</small></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="site-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 4 to 6. Add or change a contact ----

export function contactForm(site: Site, contact: Contact | null, entry: ContactInput | null, busy: boolean, error = ""): string {
  const v: ContactInput = entry ?? {
    name: contact?.name ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
    role: contact?.role ?? "",
    isActive: contact?.isActive ?? true,
    usedFor: { alerts: false, escalation: false, eewpApprover: false, eewpAwareness: false, lotoAwareness: false, ...(contact?.usedFor ?? {}) },
  };
  const roles: string[] = [...CONTACT_ROLES];
  // A role copied from the Sheet ("Safety") stays on its contact until someone picks from the list.
  if (contact?.role && !roles.includes(contact.role)) roles.unshift(contact.role);
  const options = `<option value=""${v.role ? "" : " selected"} disabled></option>${roles
    .map((r) => `<option${r === v.role ? " selected" : ""}>${esc(r)}</option>`)
    .join("")}`;
  const ticks = USED_FOR.map(
    (u) => `<label class="tick"><input type="checkbox" name="${u.key}"${v.usedFor[u.key] ? " checked" : ""}><span class="t"><b>${esc(u.label)}</b><small>${esc(u.hint)}</small></span></label>`,
  ).join("");
  return `<a class="back" href="#/sites/${esc(site.siteNumber)}">Cancel</a>
    ${ident(site.siteNumber, contact ? contact.name || contact.email : "Add Contact", `Site ${site.siteNumber} · ${site.name}`)}
    <form class="box" id="contact-form" data-site="${esc(site.siteNumber)}" data-contact="${esc(contact?.contactId ?? "")}" novalidate>
      <div class="boxhd"><div class="nm"><b>Contact</b>${contact ? `<small class="mono">${esc(contact.contactId)}</small>` : ""}</div></div>
      <div class="rows">
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(v.name)}" placeholder="Full name" maxlength="80" autocomplete="off" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Email</span><span class="v"><input class="fin wide" type="email" name="email" value="${esc(v.email)}" placeholder="name@alltech.com" autocomplete="off" required></span><span class="e"></span></label>
        <label class="r"><span class="k">Phone (optional)</span><span class="v"><input class="fin wide" type="tel" name="phone" value="${esc(v.phone)}" placeholder="Mobile number" maxlength="30" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">Role</span><span class="v"><select class="fin wide fill" name="role" required>${options}</select>
          <small class="fhint">EEWP approvers are ticked under Used For. Every ticked approver must say yes.</small></span><span class="e"></span></label>
        ${
          contact
            ? `<label class="r"><span class="k">Active</span><span class="v"><span class="tick inline"><input type="checkbox" name="isActive"${v.isActive ? " checked" : ""}><span class="t"><b>Active</b><small>Off removes them from every workflow. Their name stays on past records.</small></span></span></span><span class="e"></span></label>`
            : ""
        }
      </div>
      <div class="boxhd sub"><div class="nm"><b>Used For</b><small>Workflows pull the active contacts at the site with the matching tick.</small></div></div>
      <div class="ticks">${ticks}</div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="contact-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 9 and 10. Asset list, read only ----

export function assetListShell(site: Site, total: number, search: string): string {
  return `<a class="back" href="#/sites/${esc(site.siteNumber)}">${esc(site.name)}</a>
    ${ident(site.siteNumber, `Asset List · ${total.toLocaleString("en-US")}`, `Site ${site.siteNumber} · ${site.name}`)}
    <label class="search"><span class="sr">Search</span><input id="asset-search" class="fin wide" type="search" value="${esc(search)}" placeholder="Search Asset ID or type" autocomplete="off" autocapitalize="off" spellcheck="false"></label>
    <div id="asset-groups"></div>
    <p class="ro">Tap an asset for its page. Changes happen in the Assessment Sheet.</p>`;
}

export function assetGroups(groups: AssetGroup[], search: string): string {
  if (!groups.length) {
    return `<div class="box"><div class="empty">${search.trim() ? `Nothing matches "${esc(search.trim())}".` : "No assets at this site yet."}</div></div>`;
  }
  return groups
    .map(
      (g) => `<div class="grp"><span>${esc(g.location)}</span><span>${g.assets.length}</span></div>
      <div class="box">${g.assets
        .map(
          (a) => `<a class="ar" href="#/sites/${esc(a.assetId.slice(0, 2))}/assets/${encodeURIComponent(a.assetId)}"><span class="chip">${esc(a.assetId)}</span><span class="t">${esc(a.equipmentType)}</span><span class="chev">›</span></a>`,
        )
        .join("")}</div>`,
    )
    .join("");
}

// ---- One asset (piece 6): the QR scan count and Reset Scan Count ----

export interface ScanState {
  /** Null while it loads. A string when it could not be read. */
  count: { count: number; line: string } | string | null;
  confirming: boolean;
  busy: boolean;
  error: string;
}

/** The asset page itself lives in asset-page-screens.ts (ONE_ASSET_PAGE_WORDS.md). */

// ---- Demo Set card ----

export function demoList(data: SitesData): string {
  const rows = DEMO_TEMPLATES.map((t) => {
    const d = data.demo[t.key];
    if (!d) return `<div class="menu-row dim"><span class="num">--</span><span class="t"><b>${esc(t.name)}</b><small>Not on dev yet</small></span></div>`;
    return `<a class="menu-row" href="#/sites/demo/${t.key}"><span class="num">${t.key.toUpperCase().slice(0, 3)}</span>
      <span class="t"><b>${esc(t.name)}</b><small><span class="mono">${esc(d.assetIdPattern ?? "")}</span> · ${esc(d.equipmentType ?? "")}</small></span><span class="chev">›</span></a>`;
  }).join("");
  return `${ident("25", "Demo Set", "3 templates · the same at every site")}
    <div class="note green">A demo label at any site opens its template with that site's name. NN is the site number. Demo work counts toward training and stays off site counts and compliance.</div>
    <div class="box">${rows}</div>
    <p class="ro">Read only.</p>`;
}

function yesNo(v: unknown): string {
  return v === true ? "Yes" : v === false ? "No" : '<span class="muted">Not set</span>';
}

function txt(v: unknown): string {
  return v === undefined || v === null || v === "" ? '<span class="muted">Not set</span>' : esc(v);
}

export function demoDetail(key: string, d: DemoDoc): string {
  const name = DEMO_TEMPLATES.find((t) => t.key === key)?.name ?? key;
  const a = d as Record<string, unknown>;
  const ie = typeof a.incidentEnergyCal === "number" ? `${a.incidentEnergyCal} cal/cm²` : txt(a.ieStatus);
  return `<a class="back" href="#/sites/demo">Demo Set</a>
    ${ident(key.toUpperCase().slice(0, 3), name, String(a.assetIdPattern ?? ""))}
    <div class="box"><div class="boxhd"><div class="nm"><b>Asset</b></div></div><div class="rows">
      ${row("Asset ID pattern", `<span class="mono">${esc(a.assetIdPattern ?? "")}</span>`)}
      ${row("Equipment Type", txt(a.equipmentType))}
      ${row("Location", txt(a.location))}
      ${row("Circuit", txt(a.circuitDescription))}
      ${row("Nominal Voltage", txt(a.nominalVoltage))}
      ${row("Conductor", txt(a.conductorType))}
    </div></div>
    <div class="box"><div class="boxhd"><div class="nm"><b>Shock</b></div></div><div class="rows">
      ${row("Shock Hazard", yesNo(a.hasShockHazard))}
      ${row("Limited Approach", txt(a.limitedApproach))}
      ${row("Restricted Approach", txt(a.restrictedApproach))}
      ${row("Properly Installed", yesNo(a.isProperlyInstalled))}
      ${row("Used Per Instructions", yesNo(a.isUsedPerInstructions))}
    </div></div>
    <div class="box"><div class="boxhd"><div class="nm"><b>Arc Flash and Condition</b><small>The scenario the template plays</small></div></div><div class="rows">
      ${row("Incident Energy", ie)}
      ${row("Arc Flash Boundary", txt(a.arcFlashBoundary))}
      ${row("Available Fault Current", typeof a.availableFaultCurrentKa === "number" ? `${a.availableFaultCurrentKa} kA` : txt(a.availableFaultCurrentKa))}
      ${row("Overduty", yesNo(a.isOverduty))}
      ${row("Condition of Maintenance", txt(a.conditionOfMaintenance))}
      ${row("Doors Closed and Secured", yesNo(a.doorsClosedSecured))}
      ${row("Covers in Place", yesNo(a.coversInPlace))}
      ${row("Impending Failure Present", yesNo(a.impendingFailurePresent))}
      ${row("Properly Maintained", yesNo(a.properlyMaintained))}
    </div></div>
    <p class="ro">Read only.</p>`;
}
